import React, { memo, useCallback, useMemo, useState } from 'react';
import Router from 'next/router';

import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';

import { Label } from '@r1-frontend/ui-react/components/label';
import StaticDesktopRouter from '@r1-frontend/ui-react/organisms/Card/StaticDesktopRouter/index';
import { truncate } from '@r1-frontend/shared/helpers/truncate';
import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { fullBuyEquipAnalyticsBase } from '~/src/analytics';
import { calculateEquipmentProps } from '~/src/api/content/shop/helpers';
import { SelectPurchaseOptionPopup } from '~/src/components/SelectPurchaseOptionPopup';
import { TConfirmParams, TTestDriveData } from '~/src/components/SelectPurchaseOptionPopup/types';
import { getPriceOptions } from '~/src/entities/equipment';
import { convertShopPrices, EPriceOperationTypes, IPriceOptionProps } from '~/src/entities/price';
import { isCreditAbnEnabled } from '~/src/features/work-in-home-ab/components/Description/components/Credit/constants/abnVariants';
import { sortFunctionWithRequestIdOnTop } from '~/src/helpers/functions';

interface IDeviceGridItem {
    item: IEquipment,
    baseRoute: string,
    isCharacteristics: boolean,
}

const DeviceGridItem = ({ item, baseRoute, isCharacteristics = true }: IDeviceGridItem) => {
    const [testDriveData, setTestDriveData] = useState<TTestDriveData>();
    const [indexOfSelected, setIndexOfSelected] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    const isAbTestCreditEnabled = useMemo(() => isCreditAbnEnabled(), []);

    const priceOptions = useMemo(() => {
        // TODO добавить возможность передавать параметры обьектом
        const options: IPriceOptionProps[] = getPriceOptions(
            convertShopPrices(item),
            null,
            false,
            sortFunctionWithRequestIdOnTop,
        );

        if (item.testDrive) {
            item.testDrive.pricesAfter.forEach(variant => {
                const { id, type, duration: variantDuration, value } = variant;

                const isLeasing = type === EPriceTypes.Leasing;

                const uniqueTestDriveVariant = options.findIndex(({ id: optionId = 0 }) => optionId === id);

                if (uniqueTestDriveVariant < 0) {
                    options.push({
                        id,
                        label: isLeasing ? `Рассрочка на ${variantDuration} мес.` : 'Полная стоимость',
                        operType: variantDuration > 0 ? EPriceOperationTypes.Leasing : EPriceOperationTypes.Possess,
                        price: value,
                        type,
                        value: variantDuration,
                    });
                }
            });
        }

        return options;
    }, [item]);

    const route = useMemo(() => {
        const routePath = `${baseRoute}/${item.alias ? item.alias : item.id}`.replace('//', '/');
        const testDriveQuery = testDriveData
            ? `&testDrive=1&duration=${priceOptions[indexOfSelected].value}`
            : '';

        return `${routePath}?withOption=${indexOfSelected}${testDriveQuery}`;
    }, [baseRoute, item, indexOfSelected, testDriveData, priceOptions]);

    const category = item.category?.name;

    /**
     * Метод вынесен наружу, потому что он будет открывать модальное окно с превью.
     * На данный момент нужен, чтобы не превращать иконку в ссылку - сделано для SEO
     *
     * @param item
     */
    const onDetail = useCallback(() => {
        if (category) {
            fullBuyEquipAnalyticsBase.clickedToOrder({
                equipmentTitle: item?.title,
                category,
                leasing: (priceOptions[indexOfSelected].value ?? 0),
                cost: priceOptions[indexOfSelected].price,
            });
        }
        Router.push(route);
    }, [category, route, item?.title, priceOptions, indexOfSelected]);

    const onClose = () => {
        setIsOpen(false);
    };

    const onOpen = () => {
        setIsOpen(true);
    };

    const changePrice = ({ option, testDriveOption }: TConfirmParams) => {
        const indexOfOption = priceOptions.findIndex(({ id }) => id === option.id);

        if (~indexOfOption) {
            setIndexOfSelected(indexOfOption);
        }
        setTestDriveData(testDriveOption);
        setIsOpen(false);
    };

    const prepareCharacteristics = useMemo(() => {
        // @ts-ignore https://ticket.ertelecom.ru/browse/WEB-42809
        const props = calculateEquipmentProps(item);

        return props
            .filter((characteristic) => {
                if (typeof characteristic.value === 'string' || Array.isArray(characteristic.value)) {
                    return characteristic.value.length <= 22;
                }
                return false;
            })
            .slice(0, 3)
            .map(item => ({ ...item, value: String(item.value) }));
    }, [item.props]);

    const labels = item.labels.map(item => <Label bgColor={item.color} key={item.title} textColor={item.colorText}>{item.title}</Label>);

    const { price, oldPrice, period } = useMemo(() => {
        const { priceDuring, duration } = testDriveData || {};
        const { price: _price, pricePromo, value } = priceOptions[indexOfSelected];

        const fullCost = priceOptions[priceOptions.length - 1].price;
        const finalPrice = isAbTestCreditEnabled && !pricePromo ? fullCost : (priceDuring ?? pricePromo ?? _price);
        return {
            price: finalPrice,
            oldPrice: (!priceDuring && pricePromo) ? _price : undefined,
            period: duration ?? value ?? 0,
        };
    }, [indexOfSelected, priceOptions, testDriveData]);

    return (
        <>
            <StaticDesktopRouter
                labels={labels}
                image={item.imageWebp ? item.imageWebp : item.images[0]}
                title={truncate(item.title, 19)}
                description={item.category?.title || 'Wi-Fi роутер'}
                characteristics={isCharacteristics ? prepareCharacteristics : item.body}
                price={price}
                previousPrice={oldPrice}
                period={period}
                onButtonClick={onDetail}
                onCostClick={isAbTestCreditEnabled ? undefined : onOpen}
                buttonName={'Заказать'}
                entityUriPath={route}
                isTestDrive={!!testDriveData}
                {...(isAbTestCreditEnabled && { isCredit: !!item.isLoanAvailable })}
            />
            <SelectPurchaseOptionPopup
                isOpen={isOpen}
                equipment={item}
                onConfirmSelect={changePrice}
                onClose={onClose}
            />
        </>
    );
};

export default memo(DeviceGridItem);
